<template>
  <div class="flex min-h-full flex-1 flex-col justify-center pt-24 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <img class="mx-auto" src="/assets/images/Knocknoc_long_darkbg_Large_redish.png" alt="Knocknoc logo" />
    </div>

    <div class="mt-6 flex flex-col gap-6 sm:mx-auto sm:w-full sm:max-w-sm">
      <span class="text-center text-xl font-semibold tracking-tight">{{ t("msg.adminLogin") }}</span>

      <template v-if="useSaml">
        <button
          id="login-sso-button"
          @click="samlRedirect"
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:outline-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0"
        >
          {{ t("msg.sso") }}
        </button>

        <div class="relative">
          <div class="absolute inset-0 flex items-center" aria-hidden="true">
            <div class="w-full border-b" />
          </div>
          <div class="relative flex justify-center text-sm font-medium leading-6">
            <span class="bg-background px-6 text-white">{{ t("msg.or") }}</span>
          </div>
        </div>

        <button
          type="button"
          @click="showForm = !showForm"
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0"
        >
          {{ t("msg.localLogin") }}
          <ChevronDownIcon v-if="!showForm" class="ml-2 h-5 w-5" />
          <ChevronUpIcon v-if="showForm" class="ml-2 h-5 w-5" />
        </button>
      </template>

      <FormKit
        v-if="!useSaml || showForm"
        type="form"
        :actions="false"
        :errors="[errorMessage].filter(Boolean)"
        @submit="handleLogin"
        @submit-invalid="globals.focusFirstError"
        #default="{ disabled }"
      >
        <FormKit
          type="text"
          :classes="{
            outer: '!max-w-full',
          }"
          label="Username"
          name="UserName"
          placeholder="john.doe"
        />

        <FormKit
          type="password"
          :classes="{
            outer: '!max-w-full',
          }"
          label="Password"
          name="Password"
          placeholder="••••••••••••"
          autocomplete="off"
        />

        <FormKit
          type="submit"
          :classes="{
            outer: '$reset',
            input:
              '$reset inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0 bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2',
          }"
          :disabled="isSubmitting || disabled"
        >
          <font-awesome-icon v-if="isSubmitting" icon="fa-sharp-duotone fa-solid fa-spinner-third" spin />
          {{ t("msg.login") }}
        </FormKit>
      </FormKit>
    </div>
  </div>
</template>

<script setup>
import { FormKit } from "@formkit/vue";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/24/solid";
import { onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import globals from "../../globals";
import authService from "../../services/auth.service.ts";
import { useAdminStore } from "../../pinia/admin.ts";

const { t } = useI18n();

const adminStore = useAdminStore();

const router = useRouter();

const errorMessage = ref("");

const useSaml = ref(false);
const showForm = ref(false);

const isSubmitting = ref(false);

const samlRedirect = () => {
  location.href = "/api/admin/login/saml";
};

function triggerButtonClickIfUnfocused() {
  const active = document.activeElement;
  const isInput = active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.isContentEditable);

  if (!isInput) {
    const loginButton = document.getElementById("login-sso-button");
    if (loginButton) loginButton.click();
  }
}

function onKeyDown(e) {
  if (e.key === "Enter" && useSaml.value) {
    triggerButtonClickIfUnfocused();
  }
}

async function handleLogin(fields) {
  isSubmitting.value = true;

  try {
    await adminStore.login(JSON.stringify(fields));
    await adminStore.fetchStatus({ force: true });

    if (adminStore.user.TotpEnabled) {
      if (adminStore.user.AdminTotpVerified) {
        router.push({ name: "AdminTwoFactor" });
        return;
      }

      router.push({ name: "AdminTwoFactorOnboard" });
      return;
    }

    router.push({ name: "AdminHome" });
  } catch (error) {
    errorMessage.value = globals.renderError(t, error);
  } finally {
    isSubmitting.value = false;
  }
}

onMounted(async () => {
  window.addEventListener("keydown", onKeyDown);

  try {
    const response = await authService.useAdminSAML();
    if (response.status === 200) {
      useSaml.value = true;
    }
  } catch {
    useSaml.value = false;
  }

  const user = await adminStore.fetchStatus({ force: true });
  if (!user) {
    return;
  }
  await router.push({ path: "/admin/home" });
});
onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeyDown);
});
</script>
